import React from "react";
import { useNavigate } from "react-router-dom";
import { X, Minus, Plus, Trash2 } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { formatINR, imgUrl } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

export function CartDrawer() {
  const { items, subtotal, remove, updateQty, drawerOpen, setDrawerOpen } = useCart();
  const navigate = useNavigate();

  return (
    <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
      <SheetContent className="w-full sm:max-w-md bg-[#fcfbf8] p-0 flex flex-col" data-testid="cart-drawer">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#e8e4db]">
          <SheetTitle className="font-serif text-2xl text-[#2b1b1c] flex items-center justify-between">
            Your Bag
            <span className="text-sm text-[#7c7273] font-sans">({items.length})</span>
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div className="text-center py-24">
              <p className="font-italic-serif text-xl text-[#7c7273]">Your bag awaits its first treasure.</p>
              <button
                className="btn-outline mt-6"
                onClick={() => { setDrawerOpen(false); navigate("/shop"); }}
                data-testid="cart-empty-shop-btn"
              >
                Discover Sarees
              </button>
            </div>
          ) : (
            <ul className="space-y-5">
              {items.map((it) => {
                const key = `${it.product_id}::${it.variant_id || "base"}`;
                return (
                <li key={key} className="flex gap-4" data-testid={`cart-item-${it.product_id}`}>
                  <img src={imgUrl(it.image)} alt={it.name} className="w-20 h-24 object-cover bg-[#f5f3ef]" />
                  <div className="flex-1">
                    <div className="font-serif text-[#2b1b1c] leading-tight">{it.name}</div>
                    {it.color && <div className="text-xs text-[#7c7273] mt-0.5">Colour · {it.color}</div>}
                    <div className="text-[#7a1e2b] text-sm mt-1">{formatINR(it.price)}</div>
                    <div className="flex items-center gap-3 mt-3">
                      <div className="flex items-center border border-[#e8e4db]">
                        <button
                          className="p-1 hover:bg-[#f5f3ef]"
                          onClick={() => updateQty(it.product_id, it.quantity - 1, it.variant_id)}
                          data-testid={`qty-minus-${it.product_id}`}
                        ><Minus size={12} /></button>
                        <span className="px-3 text-sm">{it.quantity}</span>
                        <button
                          className="p-1 hover:bg-[#f5f3ef]"
                          onClick={() => updateQty(it.product_id, it.quantity + 1, it.variant_id)}
                          data-testid={`qty-plus-${it.product_id}`}
                        ><Plus size={12} /></button>
                      </div>
                      <button
                        className="text-[#7c7273] hover:text-[#7a1e2b]"
                        onClick={() => remove(it.product_id, it.variant_id)}
                        data-testid={`remove-${it.product_id}`}
                      ><Trash2 size={14} /></button>
                    </div>
                  </div>
                </li>
                );
              })}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-[#e8e4db] px-6 py-5">
            <div className="flex justify-between text-sm text-[#7c7273] mb-1">
              <span>Subtotal</span><span>{formatINR(subtotal)}</span>
            </div>
            <div className="text-xs text-[#7c7273] mb-4">Shipping and taxes calculated at checkout.</div>
            <button
              className="btn-primary w-full"
              onClick={() => { setDrawerOpen(false); navigate("/checkout"); }}
              data-testid="cart-checkout-btn"
            >
              Proceed to Checkout
            </button>
            <button
              className="btn-outline w-full mt-2"
              onClick={() => { setDrawerOpen(false); navigate("/cart"); }}
              data-testid="cart-view-btn"
            >
              View Full Bag
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
